<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { projectsApi, type Project } from '@/services/api/projects';
import { analysesApi, type Analysis } from '@/services/api/analyses';
import { useAppConfirm } from '@/composables/confirm';
import { useNotifier } from '@/composables/notifier';

const auth = useAuthStore();
const confirm = useAppConfirm();
const notify = useNotifier();

const favoriteProjects = ref<Project[]>([]);
const favoriteAnalyses = ref<Analysis[]>([]);

async function loadAll(): Promise<void> {
  const userId = auth.currentUser?.id;
  if (userId === undefined) return;
  favoriteProjects.value = await projectsApi.indexFavorite(userId);
  favoriteAnalyses.value = await analysesApi.indexFavorite(userId);
}

async function removeFavoriteProject(project: Project): Promise<void> {
  const userId = auth.currentUser?.id;
  if (userId === undefined) return;
  await projectsApi.removeFavorite(userId, project.id);
  favoriteProjects.value = favoriteProjects.value.filter((p) => p.id !== project.id);
  notify.success(`Removed project "${project.title}" from favorites.`);
}

async function removeFavoriteAnalysis(analysis: Analysis): Promise<void> {
  const userId = auth.currentUser?.id;
  if (userId === undefined) return;
  const ok = await confirm({
    header: 'Remove Analysis',
    message: 'Are you sure you want to remove this analysis?',
  });
  if (!ok) return;
  await analysesApi.removeFavorite(userId, analysis.id);
  favoriteAnalyses.value = favoriteAnalyses.value.filter((a) => a.id !== analysis.id);
  notify.success(`Removed analysis "${analysis.name}" from favorites.`);
}

async function cloneProject(project: Project): Promise<void> {
  const cloned = await projectsApi.clone(project.id);
  favoriteProjects.value = [...favoriteProjects.value, cloned];
  notify.success('Project copied');
}

onMounted(loadAll);
</script>

<template>
  <main class="dashboard">
    <h1>Dashboard</h1>

    <section class="dashboard__section">
      <h2>Favorite Projects</h2>
      <ul v-if="favoriteProjects.length" class="dashboard__list">
        <li v-for="p in favoriteProjects" :key="p.id">
          <span>{{ p.title }}</span>
          <button type="button" @click="cloneProject(p)">Clone</button>
          <button type="button" @click="removeFavoriteProject(p)">Remove</button>
        </li>
      </ul>
      <p v-else>No favorite projects yet.</p>
    </section>

    <section class="dashboard__section">
      <h2>Favorite Analyses</h2>
      <ul v-if="favoriteAnalyses.length" class="dashboard__list">
        <li v-for="a in favoriteAnalyses" :key="a.id">
          <span>{{ a.name }}</span>
          <button type="button" @click="removeFavoriteAnalysis(a)">Remove</button>
        </li>
      </ul>
      <p v-else>No favorite analyses yet.</p>
    </section>
  </main>
</template>

<style scoped>
.dashboard {
  max-width: 60rem;
  margin: 0 auto;
}
.dashboard__section {
  margin-bottom: 2rem;
}
.dashboard__list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.dashboard__list li {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e5e7eb;
}
.dashboard__list li span {
  flex: 1;
}
.dashboard__list button {
  padding: 0.25rem 0.5rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  cursor: pointer;
}
</style>
